import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { updateCommissionStatus } from "@/lib/admin-audit.functions";
import { manuallyReverseCommission } from "@/lib/admin-creator-detail.functions";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Download, CheckCircle2, Clock, XCircle, Eye } from "lucide-react";

type CommissionRow = {
  id: string;
  created_at: string;
  posted_at: string | null;
  status: string;
  currency: string;
  commission_amount: number;
  gross_amount: number;
  external_order_id: string | null;
  network: string;
  creator: { handle: string; name: string } | null;
  product_title?: string;
  split?: { party: string; rate: number; amount: number; note?: string }[];
  history?: { status: string; at: string; by: string; note?: string }[];
};

const STATUSES = ["all", "tracked", "pending", "withdrawable", "approved", "paid", "rejected", "reversed"] as const;
type StatusFilter = (typeof STATUSES)[number];

export const Route = createFileRoute("/_authenticated/admin/commissions")({
  head: () => ({
    meta: [
      { title: "Commissions — Admin" },
      { name: "description", content: "Review, approve, reject or mark paid affiliate commissions across creators." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CommissionsAdmin,
});

function fmt(n: number, ccy = "GBP") {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(n); }
  catch { return `£${n.toFixed(2)}`; }
}

function CommissionsAdmin() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [creatorHandle, setCreatorHandle] = useState("");
  const [network, setNetwork] = useState<"all" | "levanta" | "impact" | "amazon">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState<CommissionRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const list = useQuery({
    queryKey: ["admin-commissions", status, network, creatorHandle, dateFrom, dateTo],
    queryFn: async () => {
        const hasCreatorFilter = creatorHandle.trim().length > 0;
        let q = supabase
          .from("commissions")
          .select(
            hasCreatorFilter
              ? "id,created_at,posted_at,status,currency,commission_amount,gross_amount,external_order_id,network,creator:creators!inner(handle,name)"
              : "id,created_at,posted_at,status,currency,commission_amount,gross_amount,external_order_id,network,creator:creators(handle,name)",
          )
          .order("created_at", { ascending: false })
          .limit(500);
        if (status !== "all") q = q.eq("status", status);
        if (network !== "all") q = q.eq("network", network);
        if (hasCreatorFilter) q = q.ilike("creator.handle", `%${creatorHandle.trim()}%`);
        if (dateFrom) q = q.gte("created_at", new Date(dateFrom).toISOString());
        if (dateTo) {
          const end = new Date(dateTo);
          end.setHours(23, 59, 59, 999);
          q = q.lte("created_at", end.toISOString());
        }
        const { data, error } = await q;
        if (error || !data) return [] as CommissionRow[];
        return data as unknown as CommissionRow[];
    },
  });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return list.data ?? [];
    return (list.data ?? []).filter((r) =>
      r.creator?.handle?.toLowerCase().includes(s) ||
      r.creator?.name?.toLowerCase().includes(s) ||
      r.external_order_id?.toLowerCase().includes(s),
    );
  }, [list.data, search]);

  const totals = useMemo(() => {
    const t = { pending: 0, approved: 0, paid: 0, gross: 0 };
    for (const r of filtered) {
      const a = Number(r.commission_amount || 0);
      t.gross += Number(r.gross_amount || 0);
      if (r.status === "pending") t.pending += a;
      else if (r.status === "approved") t.approved += a;
      else if (r.status === "paid") t.paid += a;
    }
    return t;
  }, [filtered]);

  const callUpdate = useServerFn(updateCommissionStatus);
  const callReverse = useServerFn(manuallyReverseCommission);
  const updateStatus = useMutation({
    mutationFn: async ({ id, next }: { id: string; next: string }) => {
      await callUpdate({ data: { commissionId: id, status: next as any } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-commissions"] });
      qc.invalidateQueries({ queryKey: ["admin-audit-log"] });
    },
  });

  const reverseCommission = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await callReverse({ data: { commissionId: id, reason } });
      if (!res?.ok) throw new Error(res?.error || "Failed to reverse");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-commissions"] });
      qc.invalidateQueries({ queryKey: ["admin-audit-log"] });
    },
  });

  function promptReverse(id: string) {
    const reason = window.prompt(
      "Reason for reversal (e.g. 'Amazon return', order #, customer ref):",
      "",
    );
    if (!reason || !reason.trim()) return;
    reverseCommission.mutate({ id, reason: reason.trim() });
  }

  const selectedRows = useMemo(
    () => filtered.filter((r) => selectedIds.has(r.id)),
    [filtered, selectedIds],
  );
  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
  const someVisibleSelected = filtered.some((r) => selectedIds.has(r.id));

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    if (allVisibleSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((r) => r.id)));
  }

  async function runBulk(next: "approved" | "paid" | "rejected") {
    const ids = selectedRows
      .filter((r) => {
        if (next === "approved") return r.status === "pending";
        if (next === "paid") return r.status === "pending" || r.status === "approved";
        if (next === "rejected") return r.status !== "rejected" && r.status !== "paid" && r.status !== "reversed";
        return false;
      })
      .map((r) => r.id);
    if (ids.length === 0) return;
    setBulkRunning(true);
    try {
      for (const id of ids) {
        await callUpdate({ data: { commissionId: id, status: next as any } });
      }
    } finally {
      setBulkRunning(false);
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["admin-commissions"] });
      qc.invalidateQueries({ queryKey: ["admin-audit-log"] });
    }
  }

  const canApprove = selectedRows.some((r) => r.status === "pending");
  const canPay = selectedRows.some((r) => r.status === "pending" || r.status === "approved");
  const canReject = selectedRows.some((r) => r.status !== "rejected" && r.status !== "paid" && r.status !== "reversed");

  function exportCsv() {
    const head = ["id", "created_at", "posted_at", "status", "network", "creator", "external_order_id", "gross", "commission", "currency"];
    const rows = filtered.map((r) => [
      r.id, r.created_at, r.posted_at ?? "", r.status, r.network,
      r.creator?.handle ?? "", r.external_order_id ?? "",
      r.gross_amount, r.commission_amount, r.currency,
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `commissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl">Commissions</h2>
        <p className="mt-1 text-sm text-muted-foreground">Review, approve and mark commissions paid.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Pending" value={fmt(totals.pending)} icon={<Clock className="h-4 w-4" />} />
        <Stat label="Approved" value={fmt(totals.approved)} icon={<CheckCircle2 className="h-4 w-4" />} />
        <Stat label="Paid" value={fmt(totals.paid)} icon={<CheckCircle2 className="h-4 w-4" />} />
        <Stat label="Gross sales" value={fmt(totals.gross)} icon={<CheckCircle2 className="h-4 w-4" />} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`border px-3 py-1.5 text-xs uppercase tracking-widest ${
              status === s ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s}
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search creator or order id"
          className="w-full sm:ml-auto sm:w-64 border border-border bg-card px-3 py-1.5 text-sm outline-none focus:border-foreground"
        />
        <button onClick={exportCsv} className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">
          <Download className="h-3 w-3" /> CSV
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-border bg-card px-3 py-1.5 text-sm outline-none focus:border-foreground"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-border bg-card px-3 py-1.5 text-sm outline-none focus:border-foreground"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Creator handle</span>
          <input
            value={creatorHandle}
            onChange={(e) => setCreatorHandle(e.target.value)}
            placeholder="@handle"
            className="w-full sm:w-56 border border-border bg-card px-3 py-1.5 text-sm outline-none focus:border-foreground"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Network</span>
          <select
            value={network}
            onChange={(e) => setNetwork(e.target.value as typeof network)}
            className="border border-border bg-card px-3 py-1.5 text-sm outline-none focus:border-foreground"
          >
            <option value="all">All</option>
            <option value="levanta">Levanta</option>
            <option value="impact">Impact</option>
            <option value="amazon">Amazon</option>
          </select>
        </label>
        {(dateFrom || dateTo || creatorHandle || network !== "all") && (
          <button
            onClick={() => { setDateFrom(""); setDateTo(""); setCreatorHandle(""); setNetwork("all"); }}
            className="border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="border border-border bg-card">
        {selectedIds.size > 0 && (
          <div className="sticky top-0 z-10 border border-border bg-card px-4 py-3 flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">
              {selectedIds.size} selected
            </span>
            <div className="ml-auto flex items-center gap-2">
              {canApprove && (
                <button onClick={() => runBulk("approved")} disabled={bulkRunning} className="border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">Approve selected</button>
              )}
              {canPay && (
                <button onClick={() => runBulk("paid")} disabled={bulkRunning} className="border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white disabled:opacity-50">Mark paid selected</button>
              )}
              {canReject && (
                <button onClick={() => runBulk("rejected")} disabled={bulkRunning} className="border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50">Reject selected</button>
              )}
              <button onClick={() => setSelectedIds(new Set())} disabled={bulkRunning} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground disabled:opacity-50">Clear</button>
            </div>
          </div>
        )}

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Creator</th>
                <th className="px-4 py-2">Network</th>
                <th className="px-4 py-2 text-right">Gross</th>
                <th className="px-4 py-2 text-right">Commission</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!list.isLoading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">No commissions match.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border align-middle">
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.id}`}
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                    />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{r.external_order_id ?? r.id.slice(0, 8)}</td>
                  <td className="px-4 py-2">
                    {r.creator ? <>{r.creator.name} <span className="text-muted-foreground">@{r.creator.handle}</span></> : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{r.network}</td>
                  <td className="px-4 py-2 text-right">{fmt(Number(r.gross_amount), r.currency)}</td>
                  <td className="px-4 py-2 text-right">{fmt(Number(r.commission_amount), r.currency)}</td>
                  <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setSelected(r)}
                        className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background"
                        title="View details"
                      >
                        <Eye className="h-3 w-3" />
                      </button>
                      {r.status === "pending" && (
                        <button onClick={() => updateStatus.mutate({ id: r.id, next: "approved" })} disabled={updateStatus.isPending} className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">Approve</button>
                      )}
                      {(r.status === "pending" || r.status === "approved") && (
                        <button onClick={() => updateStatus.mutate({ id: r.id, next: "paid" })} disabled={updateStatus.isPending} className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white disabled:opacity-50">Mark paid</button>
                      )}
                      {r.status !== "rejected" && r.status !== "paid" && r.status !== "reversed" && (
                        <button onClick={() => updateStatus.mutate({ id: r.id, next: "rejected" })} disabled={updateStatus.isPending} title="Reject" className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"><XCircle className="h-3 w-3" /></button>
                      )}
                      {r.status !== "reversed" && r.status !== "paid" && (
                        <button
                          onClick={() => promptReverse(r.id)}
                          disabled={reverseCommission.isPending}
                          title="Mark as reversed (manual)"
                          className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-zinc-700 hover:text-white disabled:opacity-50"
                        >
                          Reverse
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="block md:hidden divide-y divide-border">
          {list.isLoading && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>
          )}
          {!list.isLoading && filtered.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No commissions match.</p>
          )}
          {filtered.map((r) => (
            <div key={r.id} className="border border-border bg-card p-4 rounded space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  aria-label={`Select ${r.id}`}
                  checked={selectedIds.has(r.id)}
                  onChange={() => toggleRow(r.id)}
                />
                <span className="ml-auto text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Creator</span>
                <p className="text-sm">{r.creator ? <>{r.creator.name} <span className="text-muted-foreground">@{r.creator.handle}</span></> : <span className="text-muted-foreground">—</span>}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Commission</span>
                <p className="text-sm font-medium">{fmt(Number(r.commission_amount), r.currency)}</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</span>
                  <div className="mt-0.5"><StatusPill status={r.status} /></div>
                </div>
                <div className="text-right">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Order</span>
                  <p className="mt-0.5 text-xs font-mono">{r.external_order_id ?? r.id.slice(0, 8)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 pt-1">
                <button
                  onClick={() => setSelected(r)}
                  className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background"
                  title="View details"
                >
                  <Eye className="h-3 w-3" /> View
                </button>
                {r.status === "pending" && (
                  <button onClick={() => updateStatus.mutate({ id: r.id, next: "approved" })} disabled={updateStatus.isPending} className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">Approve</button>
                )}
                {(r.status === "pending" || r.status === "approved") && (
                  <button onClick={() => updateStatus.mutate({ id: r.id, next: "paid" })} disabled={updateStatus.isPending} className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white disabled:opacity-50">Mark paid</button>
                )}
                {r.status !== "rejected" && r.status !== "paid" && r.status !== "reversed" && (
                  <button onClick={() => updateStatus.mutate({ id: r.id, next: "rejected" })} disabled={updateStatus.isPending} title="Reject" className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"><XCircle className="h-3 w-3" /> Reject</button>
                )}
                {r.status !== "reversed" && r.status !== "paid" && (
                  <button
                    onClick={() => promptReverse(r.id)}
                    disabled={reverseCommission.isPending}
                    className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-zinc-700 hover:text-white disabled:opacity-50"
                  >
                    Reverse
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {updateStatus.error && <p className="text-xs text-destructive">{(updateStatus.error as Error).message}</p>}

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selected && <CommissionDetail commission={selected} onClose={() => setSelected(null)} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">{icon}{label}</div>
      <p className="mt-2 font-serif text-2xl">{value}</p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone: Record<string, string> = {
    tracked: "bg-slate-100 text-slate-900",
    pending: "bg-amber-100 text-amber-900",
    withdrawable: "bg-teal-100 text-teal-900",
    approved: "bg-sky-100 text-sky-900",
    paid: "bg-emerald-700 text-white",
    rejected: "bg-rose-100 text-rose-900",
    reversed: "bg-zinc-200 text-zinc-900",
  };
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${tone[status] ?? "bg-muted text-muted-foreground"}`}>{status}</span>;
}

function CommissionDetail({ commission, onClose }: { commission: CommissionRow; onClose: () => void }) {
  const split = commission.split ?? [];
  const history = commission.history ?? [];
  const statusTone: Record<string, string> = {
    tracked: "text-muted-foreground",
    pending: "text-amber-600",
    approved: "text-sky-600",
    paid: "text-emerald-600",
    rejected: "text-rose-600",
    reversed: "text-zinc-500",
  };
  return (
    <>
      <SheetHeader className="space-y-1">
        <SheetTitle className="font-serif text-xl">Commission Detail</SheetTitle>
        <SheetDescription className="text-xs uppercase tracking-widest text-muted-foreground">
          {commission.external_order_id ?? commission.id} · {commission.network}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gross</div>
            <p className="mt-1 font-serif text-lg">{fmt(Number(commission.gross_amount), commission.currency)}</p>
          </div>
          <div className="border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Commission</div>
            <p className="mt-1 font-serif text-lg">{fmt(Number(commission.commission_amount), commission.currency)}</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground">
            <span>Creator</span>
            <span>Product</span>
          </div>
          <div className="flex items-center justify-between border border-border bg-card p-3 text-sm">
            <span>{commission.creator ? <>{commission.creator.name} <span className="text-muted-foreground">@{commission.creator.handle}</span></> : <span className="text-muted-foreground">—</span>}</span>
            <span className="text-muted-foreground">{commission.product_title ?? "—"}</span>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground">Split Breakdown</h4>
          {split.length === 0 && <p className="text-sm text-muted-foreground">No split data available.</p>}
          {split.map((s, i) => (
            <div key={i} className="flex items-center justify-between border border-border bg-card p-3">
              <div>
                <p className="text-sm font-medium">{s.party}</p>
                {s.note && <p className="text-xs text-muted-foreground">{s.note}</p>}
              </div>
              <div className="text-right">
                <p className="text-sm font-mono">{fmt(s.amount, commission.currency)}</p>
                <p className="text-[10px] text-muted-foreground">{(s.rate * 100).toFixed(0)}%</p>
              </div>
            </div>
          ))}
          {split.length > 0 && (
            <div className="flex items-center justify-between border-t border-border pt-2 text-xs uppercase tracking-widest text-muted-foreground">
              <span>Total</span>
              <span>{fmt(split.reduce((sum, s) => sum + s.amount, 0), commission.currency)}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground">Status History</h4>
          {history.length === 0 && <p className="text-sm text-muted-foreground">No history available.</p>}
          <div className="relative space-y-4 border-l border-border pl-4">
            {history.map((h, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-foreground" />
                <p className={`text-sm font-medium ${statusTone[h.status] ?? "text-foreground"}`}>
                  {h.status}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(h.at).toLocaleString()} · {h.by}
                </p>
                {h.note && <p className="mt-0.5 text-xs text-muted-foreground">{h.note}</p>}
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background"
        >
          Close
        </button>
      </div>
    </>
  );
}
